import { Fragment } from 'react';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import type { PcmpPerformanceLine, PcmpPerformanceReport } from '../../types/pcmpReports.types';

const cellClass = 'border border-neutral-300 px-2 py-1';
const numClass = 'text-right tabular-nums whitespace-nowrap';

const amount = (value: number | null) =>
  value === null
    ? EMPTY_VALUE
    : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percent = (value: number | null) => (value === null ? EMPTY_VALUE : `${value.toFixed(2)}%`);

const HEADINGS = [
  'Institutes',
  'Sales Target',
  'CMP Amount',
  'Disbursed',
  'Processing',
  'Due',
  'CMP %',
  'Net Challan',
  'Variance',
  'Due %',
] as const;

function LineRow({
  line,
  tone,
}: {
  line: PcmpPerformanceLine;
  tone: 'row' | 'subtotal' | 'total';
}) {
  return (
    <tr
      className={cn(
        tone === 'subtotal' && 'bg-neutral-50 font-semibold',
        tone === 'total' && 'bg-neutral-200 font-bold',
      )}
    >
      <td className={cn(cellClass, 'whitespace-nowrap', tone === 'row' && 'pl-4')}>{line.name}</td>
      <td className={cn(cellClass, numClass)}>{formatNumber(line.instituteCount)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.salesTarget)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.cmpAmount)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.disbursedAmount)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.processingAmount)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.due)}</td>
      <td className={cn(cellClass, numClass)}>{percent(line.cmpPercent)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.netChallan)}</td>
      <td className={cn(cellClass, numClass)}>{amount(line.variance)}</td>
      <td className={cn(cellClass, numClass)}>{percent(line.duePercent)}</td>
    </tr>
  );
}

interface PcmpPerformanceReportTableProps {
  report: PcmpPerformanceReport;
  className?: string;
}

/** Performance by division and region: a block per division, its total, then the grand total. */
export function PcmpPerformanceReportTable({ report, className }: PcmpPerformanceReportTableProps) {
  return (
    <table className={cn('w-full border-collapse text-xs', className)}>
      <thead>
        <tr>
          {['Region', ...HEADINGS].map((heading) => (
            <th
              key={heading}
              className={cn(cellClass, 'bg-neutral-100 text-center font-semibold text-neutral-900')}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {report.divisions.map((division) => (
          <Fragment key={division.division ?? 'unmapped'}>
            <tr>
              <td
                colSpan={HEADINGS.length + 1}
                className={cn(cellClass, 'bg-neutral-100 font-semibold')}
                // The colour is the API's accent for the division, nothing more.
                style={{ borderLeft: `4px solid ${division.color}` }}
              >
                {division.label}
              </td>
            </tr>
            {division.regions.map((region) => (
              <LineRow
                key={region.departmentId ?? `unassigned-${region.name}`}
                line={region}
                tone="row"
              />
            ))}
            <LineRow line={division.subtotal} tone="subtotal" />
          </Fragment>
        ))}
        <LineRow line={report.grandTotal} tone="total" />
      </tbody>
    </table>
  );
}
